import { Supabase } from '../Utils/supabase.config';

// Check if user can create communities (all users can create communities)
export async function canCreateCommunity(userId) {
  // All users can create communities (no restriction)
  return { success: true, data: true };
}

// Fetch all communities (no pagination)
export async function fetchAllCommunities(limit = null, offset = null) {
  let query = Supabase
    .from('communities')
    .select('*')
    .order('created_at', { ascending: false });
  
  // Only apply pagination if both limit and offset are provided
  if (limit && offset !== null) {
    query = query.range(offset, offset + limit - 1);
  }
  
  const { data, error } = await query;
  
  if (error) return { success: false, error };
  
  return { 
    success: true, 
    data
  };
}

// Fetch communities the user has joined
export async function fetchUserCommunities(userId) {
  const { data, error } = await Supabase
    .from('user_communities')
    .select('community_id, role')
    .eq('user_id', userId);
  if (error) return { success: false, error };
  return { success: true, data: data.map(item => ({ id: item.community_id, role: item.role })) };
}

// Join a community
export async function joinCommunity(userId, communityId, role = 'member') {
  const { data, error } = await Supabase
    .from('user_communities')
    .insert([{ user_id: userId, community_id: communityId, role }]);
  if (error) return { success: false, error };
  return { success: true, data };
}

// Create a new community
export async function createCommunity({ name, description, created_by, profile_image, is_announcement_only = false }) {
  const { data, error } = await Supabase
    .from('communities')
    .insert([{ name, description, created_by, profile_image, is_announcement_only }])
    .select()
    .single();
  if (error) return { success: false, error };
  
  // Automatically add creator as admin
  await joinCommunity(created_by, data.id, 'admin');
  
  return { success: true, data };
}

// Fetch member counts for all communities - Improved version
export async function fetchCommunityMemberCounts() {
  try {
    // Use a more reliable approach with proper SQL aggregation
    const { data, error } = await Supabase
      .from('communities')
      .select(`
        id,
        user_communities(count)
      `);
    
    if (error) {
      return { success: false, error };
    }
    
    // Convert to the expected format
    const counts = {};
    if (data) {
      data.forEach(community => {
        counts[community.id] = community.user_communities?.[0]?.count || 0;
      });
    }
    
    return { success: true, data: counts };
  } catch (err) {
    return { success: false, error: err };
  }
}

// Alternative approach: Count members for each community individually - More accurate but slower
export async function fetchCommunityMemberCountsIndividual() {
  try {
    // Get all communities first
    const { data: communities, error: communitiesError } = await Supabase
      .from('communities')
      .select('id');
    
    if (communitiesError) {
      return { success: false, error: communitiesError };
    }
    
    const counts = {};
    
    // Count members for each community individually
    for (const community of communities) {
      const { count, error: countError } = await Supabase
        .from('user_communities')
        .select('*', { count: 'exact', head: true })
        .eq('community_id', community.id);
      
      if (countError) {
        console.warn(`Error counting members for community ${community.id}:`, countError);
        counts[community.id] = 0;
      } else {
        counts[community.id] = count || 0;
      }
    }
    
    return { success: true, data: counts };
  } catch (err) {
    return { success: false, error: err };
  }
}

// Fetch member counts using a database function (bypasses RLS) - Most reliable
export async function fetchCommunityMemberCountsWithFunction() {
  try {
    const { data, error } = await Supabase
      .rpc('get_community_member_counts_paginated');
    
    if (error) {
      // Fallback to the improved regular approach
      return await fetchCommunityMemberCounts();
    }
    
    // Convert the result to the expected format
    const counts = {};
    if (data && Array.isArray(data)) {
      data.forEach(row => {
        counts[row.community_id] = parseInt(row.member_count) || 0;
      });
    }
    
    return { success: true, data: counts };
    
  } catch (err) {
    // Fallback to the improved regular approach
    return await fetchCommunityMemberCounts();
  }
}

// Fetch a single community by id
export async function fetchCommunityById(communityId) {
  const { data, error } = await Supabase
    .from('communities')
    .select('*')
    .eq('id', communityId)
    .single();
  if (error) return { success: false, error };
  return { success: true, data };
}

// Fetch posts for a community
export async function fetchCommunityPosts(communityId, limit = 20, offset = 0) {
  try {
    const { data, error } = await Supabase
      .from('posts')
      .select(`
        *,
        user: users ( id, name, image ),
        community: communities ( name, is_linkallowed ),
        postLikes (*),
        comments (count)
      `)
      .eq('community_id', communityId)
      .eq('hidden', false)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    
    if (error) {
      return { success: false, error };
    }
    
    return { success: true, data: data || [] };
  } catch (err) {
    return { success: false, error: err };
  }
}

// Fetch top posts for a community (by likes count)
export async function fetchCommunityTopPosts(communityId) {
  const { data, error } = await Supabase
    .from('posts')
    .select('*')
    .eq('community_id', communityId)
    .eq('hidden', false)
    .order('likes_count', { ascending: false })
    .limit(10);
  if (error) return { success: false, error };
  return { success: true, data };
}

// Update a community
export async function updateCommunity({ id, name, description, profile_image, is_announcement_only }) {
  const { data, error } = await Supabase
    .from('communities')
    .update({ name, description, profile_image, is_announcement_only })
    .eq('id', id)
    .select()
    .single();
  if (error) return { success: false, error };
  return { success: true, data };
}

// Get user role in a community
export async function getUserCommunityRole(userId, communityId) {
  const { data, error } = await Supabase
    .from('user_communities')
    .select('role')
    .eq('user_id', userId)
    .eq('community_id', communityId)
    .single();
  if (error) return { success: false, error };
  return { success: true, data: data?.role || null };
}

// Check if user can post announcements
export async function canPostAnnouncement(userId, communityId) {
  const { data, error } = await Supabase
    .from('user_communities')
    .select('role')
    .eq('user_id', userId)
    .eq('community_id', communityId)
    .in('role', ['admin', 'moderator'])
    .single();
  if (error) return { success: false, error };
  return { success: true, data: !!data };
}

// Get community members with roles
export async function getCommunityMembers(communityId, limit = 20, offset = 0) {
  try {
    // First get the user_communities data with pagination
    const { data: userCommunities, error: userCommunitiesError } = await Supabase
      .from('user_communities')
      .select('user_id, role, joined_at')
      .eq('community_id', communityId)
      .order('role', { ascending: false })
      .order('joined_at', { ascending: true })
      .range(offset, offset + limit - 1);
    
    if (userCommunitiesError) {
      return { success: false, error: userCommunitiesError };
    }

    if (!userCommunities || userCommunities.length === 0) {
      return { success: true, data: [], hasMore: false };
    }

    // Get user IDs
    const userIds = userCommunities.map(uc => uc.user_id);
    
    // Fetch user data separately
    const { data: users, error: usersError } = await Supabase
      .from('users')
      .select('id, name, image')
      .in('id', userIds);
    
    if (usersError) {
      return { success: false, error: usersError };
    }

    // Create a map of user data for quick lookup
    const userMap = {};
    if (users) {
      users.forEach(user => {
        userMap[user.id] = user;
      });
    }

    // Combine the data
    const members = userCommunities.map(uc => ({
      user_id: uc.user_id,
      role: uc.role,
      joined_at: uc.joined_at,
      users: userMap[uc.user_id] || { id: uc.user_id, name: 'Unknown User', image: null }
    }));

    // Check if there are more members
    const { count, error: countError } = await Supabase
      .from('user_communities')
      .select('*', { count: 'exact', head: true })
      .eq('community_id', communityId);
    
    const hasMore = countError ? false : (offset + limit) < count;
    return { success: true, data: members, hasMore };
  } catch (err) {
    return { success: false, error: err };
  }
}

// Update member role (admin only)
export async function updateMemberRole(communityId, userId, newRole) {
  const { data, error } = await Supabase
    .from('user_communities')
    .update({ role: newRole })
    .eq('community_id', communityId)
    .eq('user_id', userId)
    .select()
    .single();
  if (error) return { success: false, error };
  return { success: true, data };
}

// Remove member from community (admin only)
export async function removeMember(communityId, userId) {
  const { error } = await Supabase
    .from('user_communities')
    .delete()
    .eq('community_id', communityId)
    .eq('user_id', userId);
  if (error) return { success: false, error };
  return { success: true, data: null };
}

// Fetch announcement-only communities
export async function fetchAnnouncementCommunities() {
  const { data, error } = await Supabase
    .from('communities')
    .select('*')
    .eq('is_announcement_only', true)
    .order('created_at', { ascending: false });
  if (error) return { success: false, error };
  return { success: true, data };
} 

// Get community members with roles using database function (bypasses RLS)
export async function getCommunityMembersWithFunction(communityId, limit = 20, offset = 0) {
  try {
    
    const { data, error } = await Supabase
      .rpc('get_community_members_paginated', { 
        community_id: communityId,
        limit_param: limit,
        offset_param: offset
      });
    
    if (error) {
      // Check if it's a function not found error
      if (error.code === 'PGRST202' || error.message?.includes('function') || error.message?.includes('not found')) {
        // Function doesn't exist yet
      }
      
      // Fallback to the regular approach
      return await getCommunityMembers(communityId, limit, offset);
    }
    
    // Transform the data to match the expected structure
    const members = (data || []).map(row => ({
      user_id: row.user_id,
      role: row.role,
      joined_at: row.joined_at,
      users: {
        id: row.user_id,
        name: row.user_name || 'Unknown User',
        image: row.user_image
      }
    }));

    // Get total count for this community to determine hasMore
    const { count, error: countError } = await Supabase
      .from('user_communities')
      .select('*', { count: 'exact', head: true })
      .eq('community_id', communityId);
    
    if (countError) {
      // Estimate hasMore based on returned data
      const hasMore = data && data.length === limit;
      return { success: true, data: members, hasMore };
    }
    
    const hasMore = (offset + limit) < count;
    
    return { success: true, data: members, hasMore };
    
  } catch (err) {
    // Fallback to the regular approach
    return await getCommunityMembers(communityId, limit, offset);
  }
} 

// Get member count for a single community
export async function getCommunityMemberCount(communityId) {
  try {
    const { count, error } = await Supabase
      .from('user_communities')
      .select('*', { count: 'exact', head: true })
      .eq('community_id', communityId);
    
    if (error) {
      return { success: false, error };
    }
    
    return { success: true, data: count || 0 };
  } catch (err) {
    return { success: false, error: err };
  }
}

// Refresh member counts for all communities
export async function refreshCommunityMemberCounts() {
  try {
    // Try the database function first (most reliable)
    let result = await fetchCommunityMemberCountsWithFunction();
    
    if (!result.success) {
      // Fallback to individual counting (more accurate but slower)
      result = await fetchCommunityMemberCountsIndividual();
    }
    
    if (!result.success) {
      // Final fallback to basic approach
      result = await fetchCommunityMemberCounts();
    }
    
    return result;
  } catch (err) {
    return { success: false, error: err };
  }
} 